import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { List, LayoutGrid, Search as SearchIcon } from '../ui/icons';
import { useProperties } from '../../services/hooks/useProperties';
import { useSources } from '../../services/hooks/useSources';
import { PropertyItem } from '../../models/property';
import PropertyCard from './PropertyCard';
import PropertiesTableAdvanced from './PropertiesTableAdvanced';
import {
  Button,
  Input,
  Select,
  Alert,
  Spinner,
  ToggleButtonGroup,
  ToggleButton,
} from '../ui';

const PropertiesList: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSource, setSelectedSource] = useState<string>(
    searchParams.get('source') || ''
  );
  const getViewParam = () => {
    const param = searchParams.get('view');
    return param === 'cards' ? 'cards' : 'table';
  };
  const [viewMode, setViewMode] = useState<'table' | 'cards'>(getViewParam());
  const [currentPage, setCurrentPage] = useState(() => {
    const pageParam = Number(searchParams.get('page'));
    return Number.isFinite(pageParam) && pageParam > 0 ? pageParam : 1;
  });
  const limit = 200; // Fetch properties in batches of 200
  const PAGE_SIZE = 10;
  const highlightedPropertyId = searchParams.get('property') || '';

  // Get sources using React Query
  const { data: sources = [] } = useSources();

  const sourcesById = useMemo(() => {
    const map: Record<string, string> = {};
    sources.forEach((source) => {
      map[source.id] = source.name;
    });
    return map;
  }, [sources]);

  // Get properties using React Query
  const {
    data: propertiesData,
    isLoading,
    isFetching,
    error,
    refetch: refetchProperties,
  } = useProperties(limit);

  // Extract properties and pagination info
  const updatePageParam = useCallback(
    (page: number) => {
      const params = new URLSearchParams(searchParams);
      if (page <= 1) {
        params.delete('page');
      } else {
        params.set('page', String(page));
      }
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const handleViewChange = useCallback(
    (mode: 'table' | 'cards') => {
      setViewMode(mode);
      const params = new URLSearchParams(searchParams);
      if (mode === 'cards') {
        params.set('view', 'cards');
      } else {
        params.delete('view');
      }
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const handlePageChange = useCallback(
    (page: number) => {
      setCurrentPage(page);
      updatePageParam(page);
    },
    [updatePageParam]
  );

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    handlePageChange(1);
  };

  const applySourceFilter = (value: string) => {
    setSelectedSource(value);
    const params = new URLSearchParams(searchParams);
    if (value) {
      params.set('source', value);
    } else {
      params.delete('source');
    }
    params.delete('page');
    setSearchParams(params, { replace: true });
    setCurrentPage(1);
  };

  useEffect(() => {
    const paramSource = searchParams.get('source') || '';
    if (paramSource !== selectedSource) {
      setSelectedSource(paramSource);
    }
  }, [searchParams, selectedSource]);

  useEffect(() => {
    const paramView = searchParams.get('view');
    const normalized = paramView === 'cards' ? 'cards' : 'table';
    if (normalized !== viewMode) {
      setViewMode(normalized);
    }
  }, [searchParams, viewMode]);

  useEffect(() => {
    const pageParam = Number(searchParams.get('page'));
    if (Number.isFinite(pageParam) && pageParam > 0) {
      if (pageParam !== currentPage) {
        setCurrentPage(pageParam);
      }
      return;
    }
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  }, [searchParams, currentPage]);

  const handleSourceChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    applySourceFilter(event.target.value);
  };

  const handlePropertyClick = (property: PropertyItem) => {
    const params = new URLSearchParams(searchParams);
    params.set('property', property.id);
    const searchString = params.toString();
    navigate(`/sources/${property.sourceId}/properties/${property.id}`, {
      state: {
        from: {
          pathname: location.pathname,
          search: searchString ? `?${searchString}` : '',
        },
      },
    });
  };

  const fetchedProperties = useMemo(
    () => propertiesData?.items || [],
    [propertiesData]
  );

  const sortedFetchedProperties = useMemo(() => {
    const sorted = [...fetchedProperties];
    sorted.sort((a, b) => {
      const priceA = a.fields.price ?? 0;
      const priceB = b.fields.price ?? 0;
      if (priceA === priceB) {
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      }
      return priceB - priceA;
    });
    return sorted;
  }, [fetchedProperties]);

  const filteredProperties = useMemo(() => {
    if (!sortedFetchedProperties.length && !searchTerm && !selectedSource)
      return [];
    const normalizedSearch = searchTerm.trim().toLowerCase();
    let dataset = selectedSource
      ? sortedFetchedProperties.filter(
          (property) => property.sourceId === selectedSource
        )
      : sortedFetchedProperties;

    if (normalizedSearch) {
      dataset = dataset.filter((property) => {
        const titleMatch = property.title
          ? property.title.toLowerCase().includes(normalizedSearch)
          : false;
        const summaryMatch = property.fields.summary
          ? property.fields.summary.toLowerCase().includes(normalizedSearch)
          : false;
        const address = [
          property.fields.addressNameNumber,
          property.fields.addressStreet,
          property.fields.city,
          property.fields.postcode,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        const addressMatch = address.includes(normalizedSearch);
        return Boolean(titleMatch || summaryMatch || addressMatch);
      });
    }

    return dataset;
  }, [sortedFetchedProperties, searchTerm, selectedSource]);

  const totalPages = Math.max(1, Math.ceil(filteredProperties.length / PAGE_SIZE));

  useEffect(() => {
    if (currentPage > totalPages) {
      handlePageChange(totalPages);
    }
  }, [currentPage, totalPages, handlePageChange]);

  useEffect(() => {
    if (!highlightedPropertyId) return;
    const target = document.querySelector<HTMLElement>(
      `[data-property-highlight="${highlightedPropertyId}"]`
    );
    if (target) {
      target.scrollIntoView({ behavior: 'auto', block: 'center' });
    }
  }, [highlightedPropertyId]);

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Properties</h1>
        <ToggleButtonGroup
          value={viewMode}
          onChange={(newMode) =>
            handleViewChange(newMode as 'table' | 'cards')
          }
        >
          <ToggleButton value="table" aria-label="table view">
            <List className="h-4 w-4" />
          </ToggleButton>
          <ToggleButton value="cards" aria-label="card view">
            <LayoutGrid className="h-4 w-4" />
          </ToggleButton>
        </ToggleButtonGroup>
      </div>

      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="grid grid-cols-12 gap-4 items-end">
          <div className="col-span-12 md:col-span-6">
            <Input
              fullWidth
              label="Search Properties"
              value={searchTerm}
              onChange={handleSearchChange}
              startIcon={<SearchIcon className="h-4 w-4" />}
            />
          </div>
          <div className="col-span-12 md:col-span-4">
            <Select
              fullWidth
              label="Filter by Source"
              value={selectedSource}
              onChange={handleSourceChange}
              options={[
                { value: '', label: 'All Sources' },
                ...sources.map((source) => ({
                  value: source.id,
                  label: source.name,
                })),
              ]}
            />
          </div>
          <div className="col-span-12 md:col-span-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setSearchTerm('');
                applySourceFilter('');
              }}
            >
              Reset Filters
            </Button>
          </div>
        </div>
      </div>

      {error ? (
        <Alert variant="error" className="mb-6">
          Error loading properties: {error instanceof Error ? error.message : 'Unknown error'}
        </Alert>
      ) : isLoading || isFetching ? (
        <div className="flex justify-center p-12">
          <Spinner size="lg" className="text-primary-600" />
        </div>
      ) : filteredProperties.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <h6 className="text-lg font-medium text-gray-900">No properties found</h6>
        </div>
      ) : (
        <>
          {viewMode === 'table' ? (
            <>
            <PropertiesTableAdvanced
              properties={filteredProperties}
              onRowClick={handlePropertyClick}
              sources={sources}
              currentPage={currentPage}
              pageSize={PAGE_SIZE}
              onPageChange={handlePageChange}
              highlightedPropertyId={highlightedPropertyId}
            />
            </>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                {filteredProperties.map((property) => (
                  <PropertyCard
                    key={property.id}
                    property={property}
                    sourceName={sourcesById[property.sourceId]}
                    highlighted={highlightedPropertyId === property.id}
                    onClick={handlePropertyClick}
                  />
                ))}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default PropertiesList;
